import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { deferFocus } from "../../../helpers/focusUtils";
import { LpButton } from "../../../components/common/linchpin";
import { Mail, Phone, Search } from "../../../components/common/linchpin/LpIcons";
import { BusinessIcon } from "../../../common/CustomIcon";
import UserIcon from "../../../../assets/images/UserIcon.svg";

const ACCOUNT_SEARCH_PLACEHOLDER = "Search by company name, email, or account code...";

import "../../../../stylesheets/linchpin/components.css";
import "../../../../stylesheets/linchpin/order-create.css";
import "./AccountBlockV2.css";

/**
 * Account block — design-only port of v3's AccountSection.jsx into v2.
 *
 * BEHAVIOR is unchanged from v2's existing OrderdetailsComponent:
 *   - Same `accounts` source list (warehouse-scoped, passed in by parent)
 *   - Same `selectedAccountId` + `onSelectAccount(id)` / `onClearAccount()`
 *     callbacks the parent wires to its existing handlers (cascade, LoS reset,
 *     account-configs fetch, references prefill, on-hold filter, etc.).
 *   - Same credit-exceeded warning logic (looks up `creditsExceededAccnts`).
 *
 * VISUAL changes:
 *   - When no account: a search input with avatar-bearing dropdown (replaces
 *     the antd Select_v2 with code prefix).
 *   - When an account is selected: a chip card with avatar, name, code chip,
 *     email/phone/address chips, and a "Change Account" button (replaces the
 *     side-by-side Form.Item + ContactCard layout).
 *
 * The chip card uses `accountDetails` for the enriched fields (email, phone,
 * address) — same object the parent already fetches via `fetchAccountDetails`.
 * If `accountDetails` is empty, the chips simply hide; selecting the account
 * still works.
 *
 * Read-only flag suppresses the picker (no edits in view mode).
 */

const initials = (name) =>
  (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase() || "AC";

function AccountBlockV2({
  accounts = [],
  selectedAccountId,
  accountDetails,
  // True while the enriched account_details are being fetched — shows a skeleton
  // in the meta row instead of flashing empty chips before the data arrives.
  loadingDetails = false,
  onSelectAccount,
  onClearAccount,
  creditsExceededAccnts = [],
  readOnly = false,
  errorMessage,
  // Keyboard cascade (Mode A create): land focus in the search on first paint.
  autoFocusOnMount = false,
}) {
  // Resolve the selected account row from either the short-form `accounts`
  // list (has name/code) or the enriched `accountDetails` (has email/phone/
  // address). Merge with detail winning on overlap so we always show the most
  // complete info we have on hand.
  const selectedShort = useMemo(
    () => (selectedAccountId ? (accounts || []).find((a) => String(a.id) === String(selectedAccountId)) : null),
    [accounts, selectedAccountId]
  );
  const account = useMemo(() => {
    if (!selectedAccountId) return null;
    return { ...(selectedShort || {}), ...(accountDetails || {}), id: selectedAccountId };
  }, [selectedShort, accountDetails, selectedAccountId]);

  /* ─── search picker (only shown when no account is selected) ─────── */

  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const rootRef = useRef(null);
  const ddRef = useRef(null);
  const searchInputRef = useRef(null);

  // Chrome ignores autocomplete="off" on fields inside large order forms.
  // Unlock on pointer-down so autofill does not attach before first interaction.
  const unlockSearchInput = useCallback(() => {
    const el = searchInputRef.current;
    if (el && el.readOnly) {
      el.readOnly = false;
      el.removeAttribute("readonly");
    }
  }, []);

  // Keyboard cascade step 1: focus (and unlock — the autofill trap leaves the
  // input readOnly until first interaction) the account search when nothing is
  // selected. Fires on mount AND when the accounts payload lands: on a hard
  // reload the async warehouse->accounts data remounts this block, resetting
  // the input to its readOnly trap state — a mount-only effect left it
  // swallowing keystrokes (browser-verified). Never steals focus from a field
  // the user has already moved to.
  const hasAccounts = (accounts || []).length > 0;
  useEffect(() => {
    if (!autoFocusOnMount || selectedAccountId || readOnly) return undefined;
    const t = deferFocus(() => {
      const ae = document.activeElement;
      const userIsElsewhere =
        ae && ae !== document.body && ae !== searchInputRef.current &&
        ["INPUT", "TEXTAREA", "SELECT"].includes(ae.tagName || "") ;
      if (userIsElsewhere) return null;
      unlockSearchInput();
      return searchInputRef.current;
    }, 150);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFocusOnMount, hasAccounts, Boolean(selectedAccountId), readOnly]);

  // Filter the (already on-hold-filtered) accounts list by user query. v2
  // pre-filters its accounts via warehouseFilter; here we just narrow the
  // visible options as the user types.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = accounts || [];
    if (!q) return list.slice(0, 50);
    return list
      .filter((a) => `${a.name || ""} ${a.code || ""} ${a.email || ""}`.toLowerCase().includes(q))
      .slice(0, 50);
  }, [accounts, query]);

  // Reset highlight to top whenever the result set or open-state changes —
  // makes Enter predictable after typing. Default to the first SELECTABLE row so
  // the initial highlight never lands on a disabled (credit-exceeded) account.
  useEffect(() => {
    if (!filtered.length) { setActiveIdx(-1); return; }
    const exceeded = creditsExceededAccnts || [];
    const first = filtered.findIndex((a) => !exceeded.includes(a?.code));
    setActiveIdx(first >= 0 ? first : 0);
  }, [filtered, open, creditsExceededAccnts]);

  // Auto-scroll the active option into view during arrow-key navigation.
  useEffect(() => {
    if (!open || activeIdx < 0 || !ddRef.current) return;
    const el = ddRef.current.children[activeIdx];
    if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
  }, [activeIdx, open]);

  // Close the dropdown on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const handlePick = useCallback((acct) => {
    if (!acct) return;
    // Block picking a credit-limit-exceeded account (also covers keyboard Enter).
    if (acct.code && (creditsExceededAccnts || []).includes(acct.code)) return;
    setQuery("");
    setOpen(false);
    if (typeof onSelectAccount === "function") onSelectAccount(acct.id, acct);
  }, [onSelectAccount, creditsExceededAccnts]);

  const handleClear = useCallback(() => {
    if (typeof onClearAccount === "function") onClearAccount();
  }, [onClearAccount]);

  // Arrow / Enter / Esc / Home / End navigation on the search input.
  const onKeyDown = useCallback((e) => {
    const exceeded = creditsExceededAccnts || [];
    const isDisabled = (idx) => idx >= 0 && idx < filtered.length && exceeded.includes(filtered[idx]?.code);
    // Next selectable index from `from` scanning in `dir` (+1/-1), skipping
    // disabled (credit-exceeded) rows; stays put if none found in that direction.
    const seek = (from, dir) => {
      for (let j = from + dir; j >= 0 && j < filtered.length; j += dir) {
        if (!isDisabled(j)) return j;
      }
      return isDisabled(from) || from < 0 || from >= filtered.length ? -1 : from;
    };
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      setActiveIdx((i) => { const n = seek(i, 1); return n >= 0 ? n : i; });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      setActiveIdx((i) => { const n = seek(i, -1); return n >= 0 ? n : i; });
    } else if (e.key === "Enter") {
      if (open && activeIdx >= 0 && filtered[activeIdx]) {
        e.preventDefault();
        handlePick(filtered[activeIdx]);
      }
    } else if (e.key === "Escape") {
      if (open) { e.preventDefault(); setOpen(false); }
    } else if (e.key === "Home") {
      if (open) { e.preventDefault(); setActiveIdx(seek(-1, 1)); }
    } else if (e.key === "End") {
      if (open) { e.preventDefault(); setActiveIdx(seek(filtered.length, -1)); }
    }
  }, [open, activeIdx, filtered, handlePick, creditsExceededAccnts]);

  /* ─── derived display chips for the filled-state card ────────────── */

  const creditExceeded = useMemo(() => {
    if (!account) return false;
    return account.credit_exceeded === true
      || (account.code && (creditsExceededAccnts || []).includes(account.code));
  }, [account, creditsExceededAccnts]);

  const primaryPhone = account?.phone_number || account?.phone || "";
  // ponytail: Account carries a single phone_number — no second phone field
  // exists, so the Figma's second phone chip is intentionally not rendered.

  const displayName = useMemo(() => {
    if (!account) return "—";
    if (account.name && account.code) return `${account.name} (${account.code})`;
    return account.name || account.code || "—";
  }, [account]);

  /* ─── render ─────────────────────────────────────────────────────── */

  return (
    <div className="lp-v3 av2-root">
      <div className={["av2-card", account ? "av2-card--filled" : ""].filter(Boolean).join(" ")}>
        <div className="av2-card__head">
          <div className="av2-card__title">
            <span className="av2-card__icon">
              <BusinessIcon color="#586FCC" width="24" height="24" />
            </span>
            <span className="av2-card__title-text">Account</span>
          </div>
          {account && !readOnly && (
            <LpButton variant="secondary" size="sm" className="av2-change-btn" onClick={handleClear}>
              Change Account
            </LpButton>
          )}
        </div>

        {account ? (
          // ─── Filled state — Frame 35 / 34 / 33 / 37 / 23 ───────────
          <div className="av2-filled__body">
            <div className="av2-filled__avatar">{initials(account.name)}</div>
            <div className="av2-filled__content">
              <div className="av2-filled__name-row">
                <span className="av2-filled__name">{displayName}</span>
                {creditExceeded && (
                  <span className="oc-acct-warn" title="This account has exceeded its credit limit">
                    Credit Exceeded
                  </span>
                )}
              </div>
              <div className="av2-filled__meta">
                {loadingDetails ? (
                  <span className="av2-filled__meta-skeleton" aria-label="Loading account details">
                    <span className="av2-skel-bar" style={{ width: 110 }} />
                    <span className="av2-skel-bar" style={{ width: 160 }} />
                    <span className="av2-skel-bar" style={{ width: 120 }} />
                  </span>
                ) : (
                <>
                {account.contact_person && (
                  <span className="av2-filled__meta-item av2-filled__meta-item--contact">
                    <img src={UserIcon} alt="" width={16} height={16} />
                    <span>{account.contact_person}</span>
                  </span>
                )}
                {account.email && (
                  <span className="av2-filled__meta-item av2-filled__meta-item--brand">
                    <Mail width={16} height={16} />
                    <span>{account.email}</span>
                  </span>
                )}
                {primaryPhone && (
                  <span className="av2-filled__meta-item av2-filled__meta-item--brand">
                    <Phone width={16} height={16} />
                    <span>{primaryPhone}</span>
                  </span>
                )}
                </>
                )}
              </div>
            </div>
          </div>
        ) : (
          // ─── Empty state — search picker ────────────────────────────
          <div className="oc-acct-search av2-search" ref={rootRef}>
            <div
              className={["av2-search__field", errorMessage ? "av2-search__field--error" : ""].filter(Boolean).join(" ")}
            >
              <input
                type="text"
                name="av2_acct_autofill_trap"
                className="av2-search__autofill-trap"
                tabIndex={-1}
                autoComplete="username"
                aria-hidden="true"
              />
              <input
                type="password"
                name="av2_acct_autofill_trap_pw"
                className="av2-search__autofill-trap"
                tabIndex={-1}
                autoComplete="new-password"
                aria-hidden="true"
              />
              <span className="av2-search__icon" aria-hidden>
                <Search width={20} height={20} />
              </span>
              <input
                ref={searchInputRef}
                className="av2-search__input"
                id="av2-account-search"
                placeholder={ACCOUNT_SEARCH_PLACEHOLDER}
                value={query}
                disabled={readOnly}
                readOnly={!readOnly}
                autoComplete="one-time-code"
                spellCheck={false}
                onMouseDown={unlockSearchInput}
                onFocus={() => { unlockSearchInput(); setOpen(true); }}
                onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                onKeyDown={onKeyDown}
                role="combobox"
                aria-expanded={open}
                aria-controls="av2-acct-dd"
                aria-autocomplete="list"
                aria-activedescendant={open && activeIdx >= 0 && filtered[activeIdx] ? `av2-opt-${filtered[activeIdx].id}` : undefined}
                data-testid="account-search"
                data-form-type="other"
              />
            </div>
            {open && (
              <div className="oc-acct-dd" id="av2-acct-dd" role="listbox" ref={ddRef}>
                {filtered.length === 0 ? (
                  <div className="oc-acct-empty">
                    {(accounts || []).length === 0 ? "Loading accounts…" : "No matching accounts"}
                  </div>
                ) : (
                  filtered.map((a, i) => {
                    // Credit-limit-exceeded accounts can't be used to create an
                    // order: render the row disabled (greyed, non-selectable).
                    const isExceeded = (creditsExceededAccnts || []).includes(a.code);
                    return (
                    <div
                      key={a.id}
                      id={`av2-opt-${a.id}`}
                      className={["oc-acct-opt", i === activeIdx ? "is-active" : "", isExceeded ? "is-disabled" : ""].filter(Boolean).join(" ")}
                      role="option"
                      aria-selected={i === activeIdx}
                      aria-disabled={isExceeded}
                      style={isExceeded ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
                      onMouseEnter={() => { if (!isExceeded) setActiveIdx(i); }}
                      onMouseDown={() => { if (!isExceeded) handlePick(a); }}
                    >
                      <div className="oc-acct-avatar oc-acct-avatar--sm">{initials(a.name)}</div>
                      <div className="oc-acct-opt__info">
                        <span className="oc-acct-opt__name">{a.name || a.code}</span>
                        {a.code ? <span className="oc-acct-opt__sub">{a.code}</span> : null}
                      </div>
                      {isExceeded && (
                        <span className="oc-acct-warn" title="This account has exceeded its credit limit — order creation is blocked" style={{ marginLeft: "auto" }}>Credit Exceeded</span>
                      )}
                    </div>
                    );
                  })
                )}
              </div>
            )}
            {errorMessage && <div className="av2-error">{errorMessage}</div>}
          </div>
        )}
      </div>
    </div>
  );
}

export default React.memo(AccountBlockV2);
